import AddOnProductPicker from '@/components/products/AddOnProductPicker'
import CreateActionButton from '@/components/patterns/create-action-button'
import { DetailWell } from '@/components/patterns/detail-page'
import ProductCollectionPicker from '@/components/products/ProductCollectionPicker'
import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  useCreateAddOnSetMutation,
  useDeleteAddOnSetMutation,
  useUpdateAddOnSetMutation,
} from '@/lib/services/add-on-sets/addOnSets.query'
import { addOnSetSchema } from '@/lib/services/add-on-sets/addOnSets.schema'
import { getErrorMessage } from '@/lib/utils'
import { Link, useNavigate } from '@tanstack/react-router'
import { GripVertical, LibraryBig, PackagePlus } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import DeleteButton from './DeleteButton'
import SaveBar from './SaveBar'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Typography } from '@/components/ui/typography'
const emptyAddOnSet = {
  name: '',
  description: '',
  add_ons: [],
  applies_to_collections: [],
}

function normalizeAddOnSetValues(addOnSet) {
  return {
    ...emptyAddOnSet,
    ...addOnSet,
    description: String(addOnSet?.description ?? ''),
    add_ons: Array.isArray(addOnSet?.add_ons) ? addOnSet.add_ons : [],
    applies_to_collections: Array.isArray(addOnSet?.applies_to_collections)
      ? addOnSet.applies_to_collections
      : [],
  }
}

function normalizeAddOnSetPayload(value) {
  return {
    name: String(value?.name ?? '').trim(),
    description: String(value?.description ?? '').trim() || null,
    add_ons: Array.isArray(value?.add_ons) ? value.add_ons.map((addOn) => ({ id: addOn.id })) : [],
    applies_to_collections: Array.isArray(value?.applies_to_collections)
      ? value.applies_to_collections.map((collection) => ({ id: collection.id }))
      : [],
  }
}

function SortableAddOnPill({ addOn, isReadOnly = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(addOn.id),
    disabled: isReadOnly,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <span
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(!isReadOnly ? listeners : {})}
      className={
        !isReadOnly
          ? 'inline-flex max-w-full cursor-grab touch-none active:cursor-grabbing'
          : 'inline-flex max-w-full'
      }
    >
      <Badge size="comfortable" wrap className={isDragging ? 'opacity-60' : ''}>
        {!isReadOnly ? <GripVertical className="size-3.5" aria-hidden="true" /> : null}
        {addOn.name}
      </Badge>
    </span>
  )
}

function SelectionChip({ children }) {
  return (
    <Badge size="comfortable" wrap>
      {children}
    </Badge>
  )
}

export default function AddOnSetForm({ initialData = null, organizationId }) {
  const id = useId()
  const path = usePathHelper()
  const navigate = useNavigate()
  const { isReadOnly } = useIsReadOnly()
  const createMutation = useCreateAddOnSetMutation()
  const updateMutation = useUpdateAddOnSetMutation()
  const deleteMutation = useDeleteAddOnSetMutation()
  const [isAddOnsDialogOpen, setIsAddOnsDialogOpen] = useState(false)
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false)
  const addOnSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  )
  const activeMutation = initialData ? updateMutation : createMutation

  const form = useAppForm({
    defaultValues: normalizeAddOnSetValues(initialData),
    validators: {
      onSubmit: addOnSetSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          const payload = normalizeAddOnSetPayload(value)

          if (initialData?.id) {
            const updated = await updateMutation.mutateAsync({
              ...payload,
              addOnSetId: initialData.id,
            })
            toast.success('Add-on Collection updated successfully')
            setTimeout(() =>
              formApi.reset(
                normalizeAddOnSetValues(
                  updated ?? {
                    ...value,
                    id: initialData.id,
                    organization_id: initialData.organization_id,
                  }
                )
              )
            )
            return
          }

          const created = await createMutation.mutateAsync(payload)
          toast.success('Add-on Collection created successfully')

          if (created?.id) {
            await navigate({
              ...path.addOnSets.show(created.id).link,
              ignoreBlocker: true,
            })
          }
        } catch (error) {
          const action = initialData ? 'update' : 'creation'
          toast.error(`Add-on Collection ${action} failed`, {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  return (
    <div className="@container max-w-4xl">
      <form.AppForm>
        <form.Form>
          <div className="grid gap-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="inline-flex items-center gap-2">
                  <PackagePlus className="size-5" />
                  <Typography variant="h3">Add-on Collection Details</Typography>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <form.AppField name="name">
                  {(field) => <field.TextInput id={`${id}-name`} label="Name" />}
                </form.AppField>
                <form.AppField name="description">
                  {(field) => (
                    <field.TextareaInput
                      id={`${id}-description`}
                      label="Description"
                      placeholder="Optional internal note"
                    />
                  )}
                </form.AppField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <CardTitle className="inline-flex min-w-0 items-center gap-2">
                    <PackagePlus className="size-5 shrink-0" />
                    <Typography variant="h4">Offer these Add-ons...</Typography>
                  </CardTitle>

                  {!isReadOnly ? (
                    <CreateActionButton
                      context="inline"
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setIsAddOnsDialogOpen(true)}
                    >
                      Choose add-ons
                    </CreateActionButton>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent>
                <form.AppField name="add_ons">
                  {(field) => (
                    <div className="space-y-4">
                      {field.state.value?.length ? (
                        <DndContext
                          sensors={addOnSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={({ active, over }) => {
                            if (!over || active.id === over.id) {
                              return
                            }

                            const currentAddOns = field.state.value ?? []
                            const oldIndex = currentAddOns.findIndex(
                              (addOn) => String(addOn.id) === String(active.id)
                            )
                            const newIndex = currentAddOns.findIndex(
                              (addOn) => String(addOn.id) === String(over.id)
                            )

                            if (oldIndex < 0 || newIndex < 0) {
                              return
                            }

                            field.setValue(arrayMove(currentAddOns, oldIndex, newIndex))
                            void form.validateField('add_ons', 'change')
                          }}
                        >
                          <SortableContext
                            items={field.state.value.map((addOn) => String(addOn.id))}
                            strategy={rectSortingStrategy}
                          >
                            <div className="flex flex-wrap gap-2">
                              {field.state.value.map((addOn) => (
                                <SortableAddOnPill
                                  key={addOn.id}
                                  addOn={addOn}
                                  isReadOnly={isReadOnly}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <Typography variant="supporting" className="block">
                          No add-ons selected
                        </Typography>
                      )}

                      <Dialog open={isAddOnsDialogOpen} onOpenChange={setIsAddOnsDialogOpen}>
                        <DialogContent size="large">
                          <DialogHeader>
                            <DialogTitle>Add-ons</DialogTitle>
                            <DialogDescription>
                              Choose the add-ons shoppers can add together.
                            </DialogDescription>
                          </DialogHeader>
                          {isAddOnsDialogOpen ? (
                            <AddOnProductPicker
                              organizationId={organizationId}
                              initialData={field.state.value ?? []}
                              onConfirm={(addOns) => {
                                field.setValue(addOns)
                                void form.validateField('add_ons', 'change')
                                setIsAddOnsDialogOpen(false)
                              }}
                              onCancel={() => setIsAddOnsDialogOpen(false)}
                            />
                          ) : null}
                          <DetailWell compact>
                            <Typography variant="label" tone="muted">
                              Need a new add-on?{` `}
                              <Link
                                {...path.products.link('create')}
                                className="font-medium underline underline-offset-2"
                              >
                                Create one in products
                              </Link>
                              , then come back here.
                            </Typography>
                          </DetailWell>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </form.AppField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <CardTitle className="inline-flex min-w-0 items-center gap-2">
                    <LibraryBig className="size-5 shrink-0" />
                    <Typography variant="h4">...with products in these Collections</Typography>
                  </CardTitle>

                  {!isReadOnly ? (
                    <CreateActionButton
                      context="inline"
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setIsCollectionsDialogOpen(true)}
                    >
                      Choose collections
                    </CreateActionButton>
                  ) : null}
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <form.AppField name="applies_to_collections">
                  {(field) => (
                    <div className="space-y-4">
                      {field.state.value?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {field.state.value.map((collection) => (
                            <SelectionChip key={collection.id}>{collection.name}</SelectionChip>
                          ))}
                        </div>
                      ) : (
                        <Typography variant="supporting" className="block">
                          No collections selected
                        </Typography>
                      )}

                      <Dialog
                        open={isCollectionsDialogOpen}
                        onOpenChange={setIsCollectionsDialogOpen}
                      >
                        <DialogContent size="large">
                          <DialogHeader>
                            <DialogTitle>Collections</DialogTitle>
                            <DialogDescription>
                              Choose the product collections where shoppers should be offered these
                              add-ons.
                            </DialogDescription>
                          </DialogHeader>
                          {isCollectionsDialogOpen ? (
                            <ProductCollectionPicker
                              organizationId={organizationId}
                              collectionMembershipType={null}
                              initialData={field.state.value ?? []}
                              onConfirm={(collections) => {
                                field.setValue(collections)
                                void form.validateField('applies_to_collections', 'change')
                                setIsCollectionsDialogOpen(false)
                              }}
                              onCancel={() => setIsCollectionsDialogOpen(false)}
                            />
                          ) : null}
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </form.AppField>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between gap-3">
              {initialData?.id && !isReadOnly ? (
                <DeleteButton
                  id={initialData.id}
                  model="Add-on Collection"
                  mutation={deleteMutation}
                  path={path.addOnSets.index()}
                  disabled={deleteMutation.isPending}
                />
              ) : (
                <span />
              )}
              {!isReadOnly ? <SaveBar mutation={activeMutation} /> : null}
            </div>
          </div>
        </form.Form>
      </form.AppForm>
    </div>
  )
}
